import { CartItem, Product, Order, MarketingAttribution } from '../types/store';
import { STORE_CONFIG } from '../config/storeConfig';

const UTM_STORAGE_KEY = 'healthstore_marketing_attribution';
const TRACKED_PURCHASES_KEY = 'healthstore_tracked_purchases_v1';

/**
 * Capture and store UTM parameters & ad click IDs from current URL.
 */
export function captureAndStoreMarketingAttribution(): MarketingAttribution {
  try {
    if (typeof window === 'undefined') return {};

    const urlParams = new URLSearchParams(window.location.search);
    const existingRaw = sessionStorage.getItem(UTM_STORAGE_KEY) || localStorage.getItem(UTM_STORAGE_KEY);
    const existing: MarketingAttribution = existingRaw ? JSON.parse(existingRaw) : {};

    const attribution: MarketingAttribution = {
      ...existing,
      source: urlParams.get('utm_source') || existing.source || (document.referrer ? new URL(document.referrer, window.location.href).hostname : 'direct'),
      medium: urlParams.get('utm_medium') || existing.medium || 'organic',
      campaign: urlParams.get('utm_campaign') || existing.campaign || '',
      content: urlParams.get('utm_content') || existing.content || '',
      term: urlParams.get('utm_term') || existing.term || '',
      fbclid: urlParams.get('fbclid') || existing.fbclid || '',
      gclid: urlParams.get('gclid') || existing.gclid || '',
      ttclid: urlParams.get('ttclid') || existing.ttclid || '',
      landingUrl: existing.landingUrl || window.location.pathname + window.location.search,
      referrer: existing.referrer || document.referrer || '',
    };

    sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(attribution));
    localStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(attribution));
    return attribution;
  } catch {
    return {};
  }
}

export function getSavedMarketingAttribution(): MarketingAttribution {
  try {
    const raw = sessionStorage.getItem(UTM_STORAGE_KEY) || localStorage.getItem(UTM_STORAGE_KEY);
    return raw ? JSON.parse(raw) : captureAndStoreMarketingAttribution();
  } catch {
    return {};
  }
}

export const META_PIXEL_ID = '1041026968930278';
export const DEFAULT_TIKTOK_PIXEL_ID = 'DA86HQBC77UES9745BGG';

let activeTikTokPixelId: string = DEFAULT_TIKTOK_PIXEL_ID;
let activeMetaPixelId: string = META_PIXEL_ID;

let isTikTokEnabled: boolean = true;
let isMetaEnabled: boolean = true;

let isMetaPixelInitialized = false;
let isTikTokPixelInitialized = false;
let lastTrackedPage: string | null = null;
let lastTrackedPageTime: number = 0;

// Set to deduplicate purchase events in memory + sessionStorage
const memoryTrackedPurchases = new Set<string>();

function isPurchaseAlreadyTracked(orderIdentifier: string): boolean {
  if (!orderIdentifier) return false;
  if (memoryTrackedPurchases.has(orderIdentifier)) return true;

  try {
    if (typeof window !== 'undefined') {
      const stored = sessionStorage.getItem(TRACKED_PURCHASES_KEY);
      if (stored) {
        const parsed: string[] = JSON.parse(stored);
        if (parsed.includes(orderIdentifier)) {
          memoryTrackedPurchases.add(orderIdentifier);
          return true;
        }
      }
    }
  } catch {
    // ignore storage errors
  }
  return false;
}

function markPurchaseAsTracked(orderIdentifier: string): void {
  if (!orderIdentifier) return;
  memoryTrackedPurchases.add(orderIdentifier);

  try {
    if (typeof window !== 'undefined') {
      const stored = sessionStorage.getItem(TRACKED_PURCHASES_KEY);
      const parsed: string[] = stored ? JSON.parse(stored) : [];
      if (!parsed.includes(orderIdentifier)) {
        parsed.push(orderIdentifier);
        sessionStorage.setItem(TRACKED_PURCHASES_KEY, JSON.stringify(parsed));
      }
    }
  } catch {
    // ignore
  }
}

/**
 * Initialize Meta (Facebook & Instagram) Pixel
 */
export function initMetaPixel(customPixelId?: string, enabled: boolean = true): void {
  if (typeof window === 'undefined') return;
  isMetaEnabled = enabled;

  const pixelId = (customPixelId || activeMetaPixelId || META_PIXEL_ID).trim();
  if (!pixelId) return;
  activeMetaPixelId = pixelId;

  if (!enabled) {
    return;
  }

  try {
    const w = window as any;
    if (w.fbq && (isMetaPixelInitialized || w._fbq_initialized)) {
      if (w._fbq_last_id !== pixelId) {
        w.fbq('init', pixelId);
        w._fbq_last_id = pixelId;
      }
      isMetaPixelInitialized = true;
      return;
    }

    if (!w.fbq) {
      /* eslint-disable */
      const n = (w.fbq = function () {
        n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
      }) as any;
      if (!w._fbq) w._fbq = n;
      n.push = n;
      n.loaded = !0;
      n.version = '2.0';
      n.queue = [];
      const t = document.createElement('script');
      t.async = !0;
      t.src = 'https://connect.facebook.net/en_US/fbevents.js';
      const s = document.getElementsByTagName('script')[0];
      s?.parentNode?.insertBefore(t, s);
      /* eslint-enable */
    }

    w.fbq('init', pixelId);
    w._fbq_initialized = true;
    w._fbq_last_id = pixelId;
    isMetaPixelInitialized = true;
  } catch (err) {
    console.warn('Could not initialize Meta Pixel', err);
  }
}

/**
 * Initialize TikTok Pixel (Production Grade)
 * Automatically called on site load with saved Firestore ID (DA86HQBC77UES9745BGG)
 */
export function initTikTokPixel(customPixelId?: string, enabled: boolean = true): void {
  if (typeof window === 'undefined') return;
  isTikTokEnabled = enabled;

  const rawId = customPixelId || activeTikTokPixelId || STORE_CONFIG.defaultTikTokPixelId || DEFAULT_TIKTOK_PIXEL_ID;
  const pixelId = rawId.trim();
  if (!pixelId) return;
  activeTikTokPixelId = pixelId;

  if (!enabled) {
    return;
  }

  try {
    const w = window as any;
    
    // Inject official TikTok Pixel Base Code snippet
    if (!w.ttq) {
      /* eslint-disable */
      (function (w: any, d: any, t: any) {
        w.TiktokAnalyticsObject = t;
        var ttq = (w[t] = w[t] || []);
        ttq.methods = [
          'page',
          'track',
          'identify',
          'instances',
          'debug',
          'on',
          'off',
          'once',
          'ready',
          'alias',
          'group',
          'enableCookie',
          'disableCookie',
        ];
        ttq.setAndDefer = function (t: any, e: any) {
          t[e] = function () {
            t.push([e].concat(Array.prototype.slice.call(arguments, 0)));
          };
        };
        for (var i = 0; i < ttq.methods.length; i++) {
          ttq.setAndDefer(ttq, ttq.methods[i]);
        }
        ttq.instance = function (t: any) {
          var e = ttq._i[t] || [];
          for (var n = 0; n < ttq.methods.length; n++) {
            ttq.setAndDefer(e, ttq.methods[n]);
          }
          return e;
        };
        ttq.load = function (e: any, n: any) {
          var i = 'https://analytics.tiktok.com/i18n/pixel/events.js';
          ttq._i = ttq._i || {};
          ttq._i[e] = [];
          ttq._i[e]._u = i;
          ttq._t = ttq._t || {};
          ttq._t[e] = +new Date();
          ttq._o = ttq._o || {};
          ttq._o[e] = n || {};
          var o = document.createElement('script');
          o.type = 'text/javascript';
          o.async = !0;
          o.src = i + '?sdkid=' + e + '&lib=' + t;
          var a = document.getElementsByTagName('script')[0];
          a?.parentNode?.insertBefore(o, a);
        };
      })(window, document, 'ttq');
      /* eslint-enable */
    }

    if (w.ttq && typeof w.ttq.load === 'function') {
      if (w._ttq_loaded_id !== pixelId) {
        w.ttq.load(pixelId);
        w.ttq.page();
        w._ttq_loaded_id = pixelId;
      }
      w._ttq_initialized = true;
      isTikTokPixelInitialized = true;
    }
  } catch (err) {
    console.warn('Could not initialize TikTok Pixel:', err);
  }
}

/**
 * Returns current live TikTok Pixel diagnostic status
 */
export function getTikTokPixelStatus(): {
  isInitialized: boolean;
  isLoaded: boolean;
  activeId: string;
  isEnabled: boolean;
} {
  const w = (typeof window !== 'undefined' ? window : {}) as any;
  const isLoaded = Boolean(w.ttq && typeof w.ttq.track === 'function');
  return {
    isInitialized: isTikTokPixelInitialized,
    isLoaded,
    activeId: activeTikTokPixelId,
    isEnabled: isTikTokEnabled,
  };
}

/**
 * Track PageView in Meta and TikTok Pixels across SPA dynamic route changes
 */
export function trackPageView(pageName: string, customUrl?: string): void {
  try {
    if (typeof window === 'undefined') return;

    const now = Date.now();
    if (lastTrackedPage === pageName && now - lastTrackedPageTime < 600) {
      return;
    }
    lastTrackedPage = pageName;
    lastTrackedPageTime = now;

    const w = window as any;

    // Meta PageView
    if (isMetaEnabled && typeof w.fbq === 'function') {
      w.fbq('track', 'PageView');
    }

    // TikTok PageView
    if (isTikTokEnabled && w.ttq && typeof w.ttq.page === 'function') {
      w.ttq.page();
    }
  } catch {
    // Ignore tracking errors
  }
}

/**
 * Track ViewContent on Product Detail pages
 */
export function trackViewContent(product: Product): void {
  try {
    if (typeof window === 'undefined' || !product) return;
    const w = window as any;
    const price = typeof product.price === 'number' ? product.price : parseFloat(String(product.price)) || 0;
    const productIdStr = String(product.id);

    // Meta Pixel
    if (isMetaEnabled && typeof w.fbq === 'function') {
      w.fbq('track', 'ViewContent', {
        content_name: product.title,
        content_category: product.category || 'Supplements',
        content_ids: [productIdStr],
        content_type: 'product',
        value: price,
        currency: 'PKR',
      });
    }

    // TikTok Pixel
    if (isTikTokEnabled && w.ttq && typeof w.ttq.track === 'function') {
      w.ttq.track('ViewContent', {
        content_id: productIdStr,
        content_type: 'product',
        content_name: product.title,
        content_category: product.category || 'Supplements',
        price: price,
        value: price,
        currency: 'PKR',
      });
    }
  } catch {
    // Ignore tracking errors
  }
}

/**
 * Track AddToCart in Meta and TikTok Pixels
 */
export function trackAddToCart(product: Product, quantity: number = 1): void {
  try {
    if (typeof window === 'undefined' || !product) return;
    const w = window as any;
    const price = typeof product.price === 'number' ? product.price : parseFloat(String(product.price)) || 0;
    const qty = Math.max(1, quantity || 1);
    const totalValue = price * qty;
    const productIdStr = String(product.id);

    // Meta Pixel
    if (isMetaEnabled && typeof w.fbq === 'function') {
      w.fbq('track', 'AddToCart', {
        content_name: product.title,
        content_category: product.category || 'Supplements',
        content_ids: [productIdStr],
        content_type: 'product',
        value: totalValue,
        currency: 'PKR',
      });
    }

    // TikTok Pixel
    if (isTikTokEnabled && w.ttq && typeof w.ttq.track === 'function') {
      w.ttq.track('AddToCart', {
        content_id: productIdStr,
        content_type: 'product',
        content_name: product.title,
        quantity: qty,
        price: price,
        value: totalValue,
        currency: 'PKR',
      });
    }
  } catch {
    // Ignore tracking errors
  }
}

/**
 * Track InitiateCheckout in Meta and TikTok Pixels
 */
export function trackInitiateCheckout(items: CartItem[], total: number): void {
  try {
    if (typeof window === 'undefined' || !items || items.length === 0) return;
    const w = window as any;
    const contentIds = items.map((item) => String(item.productId || item.id));
    const contents = items.map((item) => ({
      id: String(item.productId || item.id),
      content_id: String(item.productId || item.id),
      content_name: item.title,
      quantity: item.quantity,
      item_price: item.price,
      price: item.price,
    }));
    const totalItems = items.reduce((sum, item) => sum + (item.quantity || 1), 0);

    // Meta Pixel
    if (isMetaEnabled && typeof w.fbq === 'function') {
      w.fbq('track', 'InitiateCheckout', {
        content_ids: contentIds,
        content_type: 'product',
        contents: contents,
        num_items: totalItems,
        value: total,
        currency: 'PKR',
      });
    }

    // TikTok Pixel
    if (isTikTokEnabled && w.ttq && typeof w.ttq.track === 'function') {
      w.ttq.track('InitiateCheckout', {
        contents: contents,
        num_items: totalItems,
        value: total,
        currency: 'PKR',
      });
    }
  } catch {
    // Ignore tracking errors
  }
}

/**
 * Track AddPaymentInfo (when customer selects Cash on Delivery or enters details)
 */
export function trackAddPaymentInfo(paymentType: string = 'Cash on Delivery', totalValue?: number): void {
  try {
    if (typeof window === 'undefined') return;
    const w = window as any;

    if (isMetaEnabled && typeof w.fbq === 'function') {
      w.fbq('track', 'AddPaymentInfo', {
        payment_type: paymentType,
        value: totalValue,
        currency: 'PKR',
      });
    }

    if (isTikTokEnabled && w.ttq && typeof w.ttq.track === 'function') {
      w.ttq.track('AddPaymentInfo', {
        payment_type: paymentType,
        value: totalValue,
        currency: 'PKR',
      });
    }
  } catch {
    // Ignore tracking errors
  }
}

/**
 * Track ClickButton (e.g. WhatsApp CTA, Buy Now, Contact)
 */
export function trackClickButton(buttonName: string, extraData?: Record<string, any>): void {
  try {
    if (typeof window === 'undefined') return;
    const w = window as any;

    if (isTikTokEnabled && w.ttq && typeof w.ttq.track === 'function') {
      w.ttq.track('ClickButton', {
        content_name: buttonName,
        ...extraData,
      });
    }

    if (isMetaEnabled && typeof w.fbq === 'function') {
      w.fbq('trackCustom', 'ClickButton', {
        button_name: buttonName,
        ...extraData,
      });
    }
  } catch {
    // Ignore tracking errors
  }
}

/**
 * Track Search (when customer searches products in SearchModal)
 */
export function trackSearch(query: string): void {
  try {
    if (typeof window === 'undefined' || !query.trim()) return;
    const cleanQuery = query.trim();
    const w = window as any;

    if (isTikTokEnabled && w.ttq && typeof w.ttq.track === 'function') {
      w.ttq.track('Search', {
        query: cleanQuery,
      });
    }

    if (isMetaEnabled && typeof w.fbq === 'function') {
      w.fbq('track', 'Search', {
        search_string: cleanQuery,
      });
    }
  } catch {
    // Ignore tracking errors
  }
}

/**
 * Track Purchase / CompletePayment (COD Order Confirmed & Placed)
 * Fires ONLY on successful order completion with deduplication protection.
 */
export function trackPurchase(order: Order): void {
  try {
    if (typeof window === 'undefined' || !order) return;

    // Deduplication check
    const orderKey = order.orderNumber || order.id;
    if (isPurchaseAlreadyTracked(orderKey)) {
      console.log(`[TikTok Pixel] Purchase event for order ${orderKey} already tracked; skipping duplicate.`);
      return;
    }

    const w = window as any;
    const contentIds = (order.items || []).map((item) => String(item.productId || item.id));
    const contents = (order.items || []).map((item) => ({
      id: String(item.productId || item.id),
      content_id: String(item.productId || item.id),
      content_name: item.title,
      quantity: item.quantity,
      item_price: item.price,
      price: item.price,
    }));
    const totalItems = (order.items || []).reduce((sum, item) => sum + (item.quantity || 1), 0);

    // Meta Pixel Purchase
    if (isMetaEnabled && typeof w.fbq === 'function') {
      w.fbq('track', 'Purchase', {
        content_name: `Order ${order.orderNumber}`,
        content_ids: contentIds,
        content_type: 'product',
        contents: contents,
        num_items: totalItems,
        value: order.total,
        currency: 'PKR',
        order_id: order.orderNumber,
      });
    }

    // TikTok Pixel (Both CompletePayment and PlaceAnOrder for maximum compatibility with all TikTok campaign objectives)
    if (isTikTokEnabled && w.ttq && typeof w.ttq.track === 'function') {
      w.ttq.track('CompletePayment', {
        content_id: order.orderNumber,
        contents: contents,
        num_items: totalItems,
        value: order.total,
        currency: 'PKR',
      });
      w.ttq.track('PlaceAnOrder', {
        content_id: order.orderNumber,
        contents: contents,
        num_items: totalItems,
        value: order.total,
        currency: 'PKR',
      });
    }

    // Mark as tracked to prevent duplicates
    markPurchaseAsTracked(orderKey);
  } catch (err) {
    console.warn('[Analytics] Error tracking purchase:', err);
  }
}

/**
 * Send Test Event directly from Admin Panel to verify pixel firing
 */
export function sendTikTokTestEvent(eventName: string = 'PageView', testPayload?: Record<string, any>): {
  success: boolean;
  message: string;
} {
  try {
    if (typeof window === 'undefined') {
      return { success: false, message: 'Window object not available.' };
    }
    const w = window as any;
    if (!w.ttq) {
      initTikTokPixel(activeTikTokPixelId, true);
    }
    if (w.ttq && typeof w.ttq.track === 'function') {
      w.ttq.track(eventName, {
        test_event: true,
        source: 'Admin_Verification_Panel',
        timestamp: new Date().toISOString(),
        pixel_id: activeTikTokPixelId,
        ...testPayload,
      });
      return {
        success: true,
        message: `Successfully fired test event "${eventName}" to TikTok Pixel (${activeTikTokPixelId}).`,
      };
    } else {
      return {
        success: false,
        message: 'TikTok Pixel SDK (ttq) is initializing in browser. Please retry in 2 seconds.',
      };
    }
  } catch (err: any) {
    return {
      success: false,
      message: `Failed to trigger test event: ${err?.message || 'Unknown error'}`,
    };
  }
}
